/**
 * Favourite football clubs for profile grid. Logos live at
 * `favs/football_teams/{slug}.png` on cricket8-web-content.
 */
export const PROFILE_FAV_FOOTBALL_LOGO_BASE =
  "https://cricket8-web-content.s3.us-east-1.amazonaws.com/favs/football_teams/";

/**
 * Grid order: country blocks England → Spain → Italy → Germany → France → others;
 * A–Z by club name within each block.
 */
export const PROFILE_FAV_FOOTBALL_TEAM_OPTIONS = [
  // England
  { slug: "arsenal", label: "Arsenal" },
  { slug: "chelsea", label: "Chelsea" },
  { slug: "liverpool", label: "Liverpool" },
  { slug: "manchester-city", label: "Man City" },
  { slug: "manchester-united", label: "Man Utd" },
  { slug: "tottenham-hotspur", label: "Spurs" },
  // Spain
  { slug: "atletico-madrid", label: "Atlético" },
  { slug: "barcelona", label: "Barcelona" },
  { slug: "real-madrid", label: "Real Madrid" },
  // Italy
  { slug: "milan", label: "AC Milan" },
  { slug: "inter-milan", label: "Inter" },
  { slug: "juventus", label: "Juventus" },
  { slug: "lazio", label: "Lazio" },
  { slug: "napoli", label: "Napoli" },
  { slug: "roma", label: "Roma" },
  // Germany
  { slug: "bayern-munich", label: "Bayern" },
  { slug: "borussia-dortmund", label: "Dortmund" },
  // France
  { slug: "paris-saint-germain", label: "PSG" },
  // Others
  { slug: "al-nassr", label: "Al Nassr" },
  { slug: "inter-miami", label: "Inter Miami" },
] as const;

export type ProfileFavFootballSlug = (typeof PROFILE_FAV_FOOTBALL_TEAM_OPTIONS)[number]["slug"];

const OPTION_SLUGS = PROFILE_FAV_FOOTBALL_TEAM_OPTIONS.map((t) => t.slug);
const SLUG_SET = new Set<string>(OPTION_SLUGS);
const SLUG_TO_LABEL: Record<string, string> = Object.fromEntries(
  PROFILE_FAV_FOOTBALL_TEAM_OPTIONS.map((t) => [t.slug, t.label])
);

/** Full club names for expanded favourites / tooltips (grid tiles use short `label`). */
const SLUG_TO_LONG_LABEL: Record<string, string> = {
  arsenal: "Arsenal",
  chelsea: "Chelsea",
  liverpool: "Liverpool",
  "manchester-city": "Manchester City",
  "manchester-united": "Manchester United",
  "tottenham-hotspur": "Tottenham Hotspur",
  "atletico-madrid": "Atlético Madrid",
  barcelona: "Barcelona",
  "real-madrid": "Real Madrid",
  milan: "AC Milan",
  "inter-milan": "Inter Milan",
  juventus: "Juventus",
  lazio: "Lazio",
  napoli: "Napoli",
  roma: "Roma",
  "bayern-munich": "Bayern Munich",
  "borussia-dortmund": "Borussia Dortmund",
  "paris-saint-germain": "Paris Saint-Germain",
  "al-nassr": "Al Nassr",
  "inter-miami": "Inter Miami",
};

export function footballTeamLogoUrl(slug: string): string {
  return `${PROFILE_FAV_FOOTBALL_LOGO_BASE}${encodeURIComponent(slug)}.png`;
}

/** Light crest on transparent PNG — white tile so it reads on purple UI (favourites strip / grid). */
export function footballLogoUsesWhiteTile(slug: string): boolean {
  return slug === "tottenham-hotspur" || slug === "napoli" || slug === "liverpool";
}

export function parseFavFootballFromStorage(raw: string | undefined | null): string[] {
  if (!raw?.trim()) return [];
  const parts = raw
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ordered: string[] = [];
  for (const slug of OPTION_SLUGS) {
    if (parts.includes(slug)) ordered.push(slug);
  }
  for (const p of parts) {
    if (!SLUG_SET.has(p) && !ordered.includes(p)) ordered.push(p);
  }
  return ordered;
}

export function serializeFavFootballToStorage(selected: readonly string[]): string {
  if (selected.length === 0) return "";
  const set = new Set(selected);
  const known = OPTION_SLUGS.filter((s) => set.has(s));
  const unknown = selected.filter((s) => !SLUG_SET.has(s));
  return [...known, ...unknown].join(", ");
}

export function favFootballLabel(slug: string): string {
  return SLUG_TO_LABEL[slug] ?? slug.replace(/-/g, " ");
}

export function favFootballLongLabel(slug: string): string {
  return SLUG_TO_LONG_LABEL[slug] ?? favFootballLabel(slug);
}
